/* GASSISA
=======================================================================================
* Descripción: Modal para retiro de capital de caja
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 23/03/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 24/03/2026 | Lic. José Luis Aragón Cervantes  | Agregar desglose monedas/billetes
*      3       | 24/03/2026 | Lic. José Luis Aragón Cervantes  | Agregar impresión ticket retiro
*      4       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Agregar resumen de corte en retiro y ticket
*      5       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Corregir navegación fluida con Enter y Tab
*      6       | 08/06/2026 | Lic. José Luis Aragón Cervantes  | Retiro sin validación de capital disponible
*      7       | 27/08/2026 | Lic. José Luis Aragón Cervantes  | Esperar recarga de corte después del retiro
*      8       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Botón manual de apertura de cajón
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import React, { Fragment, useState, useEffect, useRef } from "react";
import { Modal, Box, Typography, Button, Divider } from "@mui/material";
import { NumericFormat } from 'react-number-format';
import { useSesion } from "../../context/System/SesionContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { useVentas } from "../../context/Ventas/VentasContext";
import { apiRetiroCapital } from "../../settings/ApiConfig";

const listaTiposMonedas = [
    { "TIPO": "MONEDA", "VALOR": 0.50 },
    { "TIPO": "MONEDA", "VALOR": 1 },
    { "TIPO": "MONEDA", "VALOR": 2 },
    { "TIPO": "MONEDA", "VALOR": 5 },
    { "TIPO": "MONEDA", "VALOR": 10 },
    { "TIPO": "BILLETE", "VALOR": 20 },
    { "TIPO": "BILLETE", "VALOR": 50 },
    { "TIPO": "BILLETE", "VALOR": 100 },
    { "TIPO": "BILLETE", "VALOR": 200 },
    { "TIPO": "BILLETE", "VALOR": 500 },
    { "TIPO": "BILLETE", "VALOR": 1000 }
];

const modalStyle = {
    position: 'fixed',
    top: '2%',
    left: '25%',
    width: '50vw',
    maxWidth: '90vw',
    maxHeight: '95vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
    overflowY: 'auto',
};

export default function ModalRetiroCapital(props) {
    const { openModal = false, onClose, onSuccess, caja, resumenCorte = {}, usuarioAutorizado } = props;
    
    const { usuarioData, parametrosCajaForm } = useSesion();
    const usuarioRetiro = usuarioAutorizado || usuarioData?.Usuario || "";
    const { handleOpenAlert, handleOpenLoader, handleCloseLoader } = useLayout();
    const { PostRequest } = useRequest();
    const { handleAbrirCajonDinero } = useVentas();

    const [internalOpen, setInternalOpen] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [observaciones, setObservaciones] = useState("");
    const [loading, setLoading] = useState(false);
    const [listadoMonedas, setListadoMonedas] = useState(
        listaTiposMonedas.map(m => ({ ...m, Cantidad: 0 }))
    );
    const [totalConteo, setTotalConteo] = useState(0);
    
    const primerInputRef = useRef(null);
    const cantidadInputsRef = useRef([]);
    const observacionesInputRef = useRef(null);
    const retiroButtonRef = useRef(null);

    const getResumenValue = (field) => Number(resumenCorte?.[field] ?? 0) || 0;
    const resumenCorteRows = [
        { label: "Efectivo recibido", value: getResumenValue("totalefectivo") },
        { label: "Abono inicial crédito clientes", ticketLabel: "Abono inicial credito clientes", value: getResumenValue("totalefectivocredito") },
        { label: "Tarjeta débito", ticketLabel: "Tarjeta debito", value: getResumenValue("totaltarjetadebito") },
        { label: "Tarjeta crédito", ticketLabel: "Tarjeta credito", value: getResumenValue("totaltarjetacredito") },
        { label: "Transferencia", value: getResumenValue("totaltransferencia") },
        { label: "Abonos apartados", value: getResumenValue("totalabonosapartados") },
        { label: "Crédito clientes", ticketLabel: "Credito clientes", value: getResumenValue("totalcreditocliente") },
        { label: "Devoluciones", value: getResumenValue("totaldevoluciones") },
        { label: "Retiros", value: getResumenValue("totalretiros") },
        { label: "Total general", value: getResumenValue("totalgeneral"), strong: true },
    ];

    useEffect(() => {
        cantidadInputsRef.current = [];
    }, [listadoMonedas]);

    const handleKeyDown = (e, index, totalCampos) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();

            if (index < totalCampos - 1) {
                const nextInput = cantidadInputsRef.current[index + 1];
                if (nextInput) {
                    nextInput.focus();
                    nextInput.select();
                }
            } else if (index === totalCampos - 1) {
                if (observacionesInputRef.current) {
                    observacionesInputRef.current.focus();
                    observacionesInputRef.current.select();
                }
            }
        }
};

    const handleOnFocus = (e) => {
        e.target.select();
    };

    useEffect(() => {
        if (openModal) {
            setInternalOpen(true);
            setLoading(false);
            setObservaciones("");
            setListadoMonedas(listaTiposMonedas.map(m => ({ ...m, Cantidad: 0 })));
            setTimeout(() => setModalVisible(true), 50);
            setTimeout(() => {
                if (primerInputRef.current) {
                    primerInputRef.current.focus();
                    primerInputRef.current.select();
                }
            }, 100);
        } else {
            setModalVisible(false);
            setTimeout(() => setInternalOpen(false), 300);
        }
    }, [openModal]);

    useEffect(() => {
        const total = listadoMonedas.reduce((acc, item) => acc + (item.Cantidad * item.VALOR), 0);
        setTotalConteo(total);
    }, [listadoMonedas]);

    const handleUpdateListaMonedas = (tipo, valor, cantidad) => {
        const updatedList = listadoMonedas.map((item) =>
            item.TIPO === tipo && item.VALOR === valor
                ? { ...item, Cantidad: parseFloat(cantidad) || 0 }
                : item
        );
        setListadoMonedas(updatedList);
    };

    const handleRetiroCapital = async () => {
        if (totalConteo <= 0) {
            handleOpenAlert("El monto de retiro debe ser mayor a 0", "warning");
            return;
        }

        setLoading(true);
        handleOpenLoader("Procesando retiro de capital, por favor espere...");
        try {
            const listaMonedasFormateada = listadoMonedas.map(m => ({
                tipo: m.TIPO,
                valor: m.VALOR,
                cantidad: m.Cantidad
            }));

            const data = {
                id: 0,
                usuarioautorizado: usuarioRetiro,
                montoretiro: totalConteo,
                caja: caja,
                vendedor: usuarioData.Usuario,
                observaciones: observaciones,
                listacantidadmonedasbilletes: listaMonedasFormateada
            };

            const response = await PostRequest({
                url: apiRetiroCapital,
                body: data,
            });

            if (response.hasError) {
                handleOpenAlert(response.message, "error");
                setLoading(false);
                handleCloseLoader();
                return;
            }

            handleOpenAlert("Retiro de capital realizado correctamente", "success");

            imprimirTicketRetiro(data).catch((printError) => {
                handleOpenAlert("Error en impresión: " + printError.message, "warning");
            });

            if (onSuccess) {
                await onSuccess(response.result);
            }
            
            if (onClose) {
                onClose();
            }

            setLoading(false);
            handleCloseLoader();
            setTimeout(() => {
                setModalVisible(false);
                setInternalOpen(false);
            }, 100);
            return;
        } catch (error) {
            handleOpenAlert("Error al realizar retiro de capital: " + error.message, "error");
            setLoading(false);
            handleCloseLoader();
        }
    };

    const imprimirTicketRetiro = async (dataRetiro) => {
        const formData = new FormData();
        const textEncoder = new TextEncoder();

        let ticketParts = [textEncoder.encode('\x1B\x40')];
        const pushResumenCorteTicket = () => {
            ticketParts.push(
                textEncoder.encode('RESUMEN DE CORTE:\n'),
                textEncoder.encode('-'.repeat(32) + '\n'),
            );

            resumenCorteRows.forEach(row => {
                const label = `${row.ticketLabel || row.label}:`.padEnd(18);
                ticketParts.push(textEncoder.encode(label + formatCurrency(row.value).padStart(14) + '\n'));
            });

            ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n\n'));
        };

        ticketParts.push(
            textEncoder.encode('\x1B\x61\x01\x1B\x21\x11' + 'RETIRO DE CAPITAL\n'),
            textEncoder.encode('\x1B\x61\x00\x1B\x21\x00' + '-'.repeat(32) + '\n'),
            textEncoder.encode('Caja: ' + (caja || "") + '\n'),
            textEncoder.encode('Usuario: ' + (usuarioRetiro || "") + '\n'),
            textEncoder.encode('Fecha: ' + new Date().toLocaleString('es-MX') + '\n'),
            textEncoder.encode('-'.repeat(32) + '\n\n'),
        );

        pushResumenCorteTicket();

        const monedas = dataRetiro.listacantidadmonedasbilletes || [];
        if (monedas.length > 0) {
            ticketParts.push(textEncoder.encode('DESGLOSE DE EFECTIVO:\n'));
            ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n'));
            
            monedas.forEach(m => {
                if (m.cantidad > 0) {
                    const subtotal = m.cantidad * m.valor;
                    const label = m.tipo === 'MONEDA' ? 'Mda' : 'Bll';
                    ticketParts.push(textEncoder.encode(
                        `${label} $${m.valor.toFixed(2)} x ${m.cantidad} = $${subtotal.toFixed(2)}\n`
                    ));
                }
            });
            
            ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n'));
        }

        ticketParts.push(
            textEncoder.encode('\n'),
            textEncoder.encode('\x1B\x21\x11' + 'TOTAL RETIRO: ' + formatCurrency(totalConteo) + '\n'),
            textEncoder.encode('\x1B\x21\x00'),
        );

        if (observaciones) {
            ticketParts.push(textEncoder.encode('\nObs: ' + observaciones + '\n'));
        }

        ticketParts.push(
            textEncoder.encode('\n\n'),
            textEncoder.encode('\x1B\x61\x01' + 'Retiro realizado exitosamente!\n\n\n'),
            textEncoder.encode('\x1B\x64\x06'),
            textEncoder.encode('\x1D\x56\x00')
        );

        ticketParts.push(
            textEncoder.encode('\n\n'),
            textEncoder.encode('\x1B\x61\x01\x1B\x21\x11' + 'COPIA RETIRO CAPITAL\n'),
            textEncoder.encode('\x1B\x61\x00\x1B\x21\x00' + '-'.repeat(32) + '\n'),
            textEncoder.encode('Caja: ' + (caja || "") + '\n'),
            textEncoder.encode('Usuario: ' + (usuarioRetiro || "") + '\n'),
            textEncoder.encode('Fecha: ' + new Date().toLocaleString('es-MX') + '\n'),
            textEncoder.encode('-'.repeat(32) + '\n\n'),
        );

        pushResumenCorteTicket();

        ticketParts.push(
            textEncoder.encode('DESGLOSE DE EFECTIVO:\n'),
            textEncoder.encode('-'.repeat(32) + '\n'),
        );
        
        monedas.forEach(m => {
            if (m.cantidad > 0) {
                const subtotal = m.cantidad * m.valor;
                const label = m.tipo === 'MONEDA' ? 'Mda' : 'Bll';
                ticketParts.push(textEncoder.encode(
                    `${label} $${m.valor.toFixed(2)} x ${m.cantidad} = $${subtotal.toFixed(2)}\n`
                ));
            }
        });
        
        ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n'));
        ticketParts.push(
            textEncoder.encode('\n'),
            textEncoder.encode('\x1B\x21\x11' + 'TOTAL RETIRO: ' + formatCurrency(totalConteo) + '\n'),
            textEncoder.encode('\x1B\x21\x00'),
        );

        if (observaciones) {
            ticketParts.push(textEncoder.encode('\nObs: ' + observaciones + '\n'));
        }

        ticketParts.push(
            textEncoder.encode('\n\n'),
            textEncoder.encode('\x1B\x61\x01' + 'Copia retiro!\n\n\n'),
            textEncoder.encode('\x1B\x64\x06'),
            textEncoder.encode('\x1D\x56\x00')
        );

        const totalLength = ticketParts.reduce((sum, part) => sum + part.length, 0);
        const finalTicket = new Uint8Array(totalLength);
        let offset = 0;
        ticketParts.forEach(part => {
            finalTicket.set(part, offset);
            offset += part.length;
        });

        formData.append("ticket", new Blob([finalTicket], { type: "application/octet-stream" }));
        formData.append("printerName", parametrosCajaForm?.nombreimpresoraventas?.trim() || "");

        const logoUrl = `${process.env.PUBLIC_URL}/recursos/logocliente.png`;
        try {
            const logoResponse = await fetch(logoUrl);
            if (logoResponse.ok) {
                const logoBlob = await logoResponse.blob();
                formData.append("logo", logoBlob, "logocliente.png");
            }
        } catch (err) {
            handleOpenAlert("Logo no disponible:", err);
        }

        try {
            const response2 = await fetch(`http://localhost:4000/api/imprimirticket`, {
                method: "POST",
                body: formData,
            });

            if (!response2.ok) {
                handleOpenAlert("Error en envío de impresión:", response2.statusText);
            }
        } catch (error) {
            handleOpenAlert("Error en impresión:", error.message);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(value || 0);
    };

    const handleObservacionesKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            if (retiroButtonRef.current) {
                retiroButtonRef.current.focus();
            }
        }
    };

    return (
        <Modal
            open={internalOpen}
            onClose={onClose}
            closeAfterTransition
        >
            <Box sx={{ ...modalStyle, transform: modalVisible ? 'scale(1)' : 'scale(0.8)', opacity: modalVisible ? 1 : 0, transition: 'all 0.3s ease-in-out' }}>
                <Fragment>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Retiro de Capital
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Caja: <strong>{caja}</strong>
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                            Resumen del corte
                        </Typography>

                        {resumenCorteRows.map((row) => (
                            <Box
                                key={row.label}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    py: 0.35,
                                    borderTop: row.strong ? '1px solid' : 'none',
                                    borderColor: 'divider',
                                    mt: row.strong ? 0.5 : 0,
                                    pt: row.strong ? 0.85 : 0.35,
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: row.strong ? 700 : 400 }}>
                                    {row.label}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: row.strong ? 700 : 400 }}>
                                    {formatCurrency(row.value)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                        Desglose de Efectivo a Retirar
                    </Typography>

                    <Box sx={{ maxHeight: '300px', overflowY: 'auto', mb: 2 }}>
                        {listadoMonedas.map((moneda, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Typography sx={{ minWidth: '80px' }}>
                                    {moneda.TIPO} ${moneda.VALOR}
                                </Typography>
                                <NumericFormat
                                    inputRef={(el) => {
                                        if (index === 0) primerInputRef.current = el;
                                        cantidadInputsRef.current[index] = el;
                                    }}
                                    value={moneda.Cantidad}
                                    thousandSeparator={true}
                                    decimalScale={0}
                                    fixedDecimalScale
                                    customInput={TextField}
                                    label="Cantidad"
                                    sx={{ width: '150px' }}
                                    size="small"
                                    onValueChange={(values) => handleUpdateListaMonedas(moneda.TIPO, moneda.VALOR, values.value)}
                                    onKeyDown={(e) => handleKeyDown(e, index, listadoMonedas.length)}
                                    onFocus={handleOnFocus}
                                />
                                <Typography sx={{ minWidth: '100px', textAlign: 'right' }}>
                                    = {formatCurrency(moneda.Cantidad * moneda.VALOR)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Total Retiro:
                        </Typography>
                        <Typography variant="h5" color="warning.main">
                            {formatCurrency(totalConteo)}
                        </Typography>
                    </Box>

                    <TextField autoComplete="off"
                        inputRef={observacionesInputRef}
                        label="Observaciones"
                        multiline
                        rows={2}
                        fullWidth
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                        onKeyDown={handleObservacionesKeyDown}
                        onFocus={handleOnFocus}
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={() => handleAbrirCajonDinero()}
                            disabled={loading}
                        >
                            Abrir cajón
                        </Button>
                        <Button 
                            variant="outlined" 
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button 
                            ref={retiroButtonRef}
                            variant="contained" 
                            color="warning"
                            onClick={handleRetiroCapital}
                            disabled={loading || totalConteo <= 0}
                        >
                            {loading ? "Procesando..." : "Realizar Retiro"}
                        </Button>
                    </Box>
                </Fragment>
            </Box>
        </Modal>
    );
}
